<template>
    <div>
      <v-row>
        <v-col>
          <div class="d-flex justify-space-between mt-6 mb-3">
            <h2 class="setup-dataroom-title">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.yourOffer') }}</h2>
            <app-button
              v-if="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
              type="outlined"
              variant="neutral"
              @click="$emit('changeOffer')"
            >
              {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.changeOffer') }}
            </app-button>
          </div>

          <div class="text-body-1 font-weight-medium text-grey-900">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomSizeText') }}
          </div>
          <v-chip-group v-model="selectedData"
                        selected-class="bg-deep-purple text-white"
                        mandatory
                        @update:model-value="onDataRoomSettingsChange"
          >
            <v-chip v-for="item in data"
                    :key="`data-${item.value}`"
                    :value="item"
                    :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer && !areSameSizeData(item, selectedData)"
            >
              {{ item.text }}
            </v-chip>
            <v-chip class="customDataChip"
                    value="additional"
                    :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
            >
              <v-select v-model="selectedAdditionalData"
                        :items="additionalData"
                        :placeholder="$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.other')"
                        class="customDataSelect mt-0"
                        hide-details
                        item-title="text"
                        return-object
                        rounded
                        @click.stop
                        @mousedown.stop
                        @update:model-value="onSelectedAdditionalDataChange"
              />
            </v-chip>
          </v-chip-group>
          <div v-if="hasAddonData"
               class="text-caption text-deep-purple font-weight-medium mt-1"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.priceForAdditionalData', { size: computedAddonStorage, price: computedAddonStoragePrice }) }}
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-5">
        <v-col>
          <div class="text-body-1 font-weight-medium text-grey-900">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomDurationText') }}
          </div>
          <div>
            <v-chip-group v-model="selectedDuration"
                          selected-class="bg-deep-purple text-white"
                          mandatory
                          @update:model-value="onDataRoomSettingsChange"
            >
              <v-chip v-for="item in durations"
                      :key="`duration-${item.value}`"
                      :value="item"
                      :disabled="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer && item !== selectedDuration"
              >
                {{ item.text }}
              </v-chip>
            </v-chip-group>
          </div>
          <div v-if="dataRoomCustomPricing && dataRoomCustomPricing.discountPercent"
               class="text-caption text-deep-purple font-weight-medium"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discount', { discount: dataRoomCustomPricing.discountPercent }) }}
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-6">
        <v-col>
          <h2 class="setup-dataroom-title">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.offerDetails') }}</h2>

          <div class="offer-details-container bg-grey-25 rounded-lg pa-5 mt-3">
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between">
                <div>
                  <i18n-t
                    :plural="newDataRoomDuration.value"
                    keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomForSizeAndDuration"
                    scope="global"
                  >
                    <template #size>
                      <span class="text--bold">
                        {{ `${dataRoomCustomPricing.size} ${$t(`projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.${dataRoomCustomPricing.unit ?? 'Mb'}`)}` }}
                      </span>
                    </template>
                    <template #duration>
                      <span class="text--bold">
                        {{ `${dataRoomCustomPricing.months} ${$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.months', dataRoomCustomPricing.months)}` }}
                      </span>
                    </template>
                  </i18n-t>
                </div>

                <span>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.netAmountPerMonth', { amount: dataRoomCustomPricing.monthlyCost }) }}
                </span>
              </div>

              <div
                v-if="dataRoomCustomPricing.customerDiscountPercent > 0"
                class="d-flex justify-space-between text-deep-purple mt-1"
              >
                <span>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.customerDiscount', { discount: dataRoomCustomPricing.customerDiscountPercent }) }}
                </span>
                <span>
                   {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discountNetAmountPerMonth', { amount: dataRoomCustomPricing.customerDiscountAmount }) }}
                </span>
              </div>

              <div
                v-if="dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer"
                class="d-flex justify-space-between text-deep-purple mt-1"
              >
                <span>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.customerDiscountDataroomSpecialOffer', { discount: dataRoomCustomPricing.customerDiscountDataroomSpecialOfferPercent }) }}
                </span>
                <span>
                   {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discountNetAmountPerMonth', { amount: dataRoomCustomPricing.customerDiscountDataroomSpecialOfferAmount }) }}
                </span>
              </div>

              <v-divider class="my-5 border-opacity-100" color="grey-100" />

              <div class="d-flex justify-space-between">
                <span class="setup-dataroom-title">
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.totalAmount') }}
                </span>
                <span class="setup-dataroom-title">
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.netAmountPerMonth', { amount: dataRoomCustomPricing.monthlyCostWithDiscount }) }}
                </span>
              </div>

              <p class="mt-6 text-grey-800">
                <i18n-t
                  :plural="newDataRoomDuration.value"
                  class="mt-2"
                  keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.commitment"
                  scope="global"
                >
                  <template #duration>
                  <span class="text--small text--bold">
                    <span v-t="{
                              path: 'projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount',
                              plural: newDataRoomDuration.value,
                              args: { months: newDataRoomDuration.value },
                            }"
                    />
                  </span>
                  </template>
                  <template #price>
                  <span class="text--small text--bold">
                    {{ $t('common.price.excludingVAT', { amount: dataRoomCustomPricing.monthlyCostWithDiscount }) }}
                  </span>
                  </template>
                </i18n-t>
              </p>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-6 mb-1">
        <v-col>
          <div>
            <v-checkbox v-model="validator.$model"
                        :error-messages="termsCheckedError"
                        class="mt-0 pt-0"
                        :hide-details="termsCheckedError.length === 0"
            >
              <template #label>
                <span>{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text1') }}
                  <a href="#" @click.stop>
                    <AppDownloadFile :link="dataroomTermsLink">
                      <template #default="{ submitForm }">
                        <span
                          class="text-darkblue-500 app-link text-decoration-underline"
                          @click="submitForm"
                        >
                          {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text2') }}
                        </span>
                      </template>
                    </AppDownloadFile>
                  </a>
                  {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text3') }}
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapActions, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { formFieldErrors } from '@/common/utils/form'
import { GET_DATA_ROOM_CUSTOM_PRICING } from '@/store/modules/rooms/action_types'

const ADDITIONAL_DATA_VALUE = 'additional'

export default {
  name: 'ProjectsNewDataroomDialogSetupDataRoom',
  components: { AppDownloadFile },
  props: {
    newDataRoom: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      selectedDuration: null,
      selectedData: null,
      selectedAdditionalData: null,
      durations: [
        { value: 1, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 1 }, 1) },
        { value: 3, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 3 }, 3) },
        { value: 6, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 6 }, 6) },
        { value: 12, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 12 }, 12) },
      ],
      data: [
        { value: 500, unit: 'Mb', text: `500 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Mb')}` },
        { value: 1, unit: 'Gb', text: `1 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 2, unit: 'Gb', text: `2 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 5, unit: 'Gb', text: `5 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
      ],
      additionalData: [
        { value: 10, unit: 'Gb', text: `10 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 15, unit: 'Gb', text: `15 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 20, unit: 'Gb', text: `20 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 25, unit: 'Gb', text: `25 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 30, unit: 'Gb', text: `30 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
      ],
    }
  },
  computed: {
    ...mapState('rooms', ['dataRoomCustomPricing', 'dataRoomCustomPricingPending']),
    newDataRoomData: {
      get () {
        return {
          value: this.newDataRoom.storage,
          unit: this.newDataRoom.unit,
        }
      },
      set (data) {
        this.newDataRoom.storage = data.value
        this.newDataRoom.unit = data.unit
      },
    },
    newDataRoomDuration: {
      get () {
        return {
          value: this.newDataRoom.duration,
        }
      },
      set (duration) {
        this.newDataRoom.duration = duration.value
      },
    },
    computedAddonStorage () {
      if (this.hasAddonData) {
        const size = this.dataRoomCustomPricing.additionalStorageSize
        if (size < 1) {
          return `${size * 1000} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Mb')}`
        } else {
          return `${size} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}`
        }
      }
      return null
    },
    computedAddonStoragePrice () {
      return this.dataRoomCustomPricing.customerDiscountPercent
        ? this.dataRoomCustomPricing.additionalStoragePriceWithDiscount
        : this.dataRoomCustomPricing.additionalStoragePrice
    },
    hasAddonData () {
      if (this.dataRoomCustomPricing) {
        return !!(this.dataRoomCustomPricing.additionalStorageSize && this.dataRoomCustomPricing.additionalStoragePrice)
      }
      return null
    },
    termsCheckedError () {
      return formFieldErrors(
        this.validator,
        [
          ['sameAs', this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.termsCheckedValidationError')],
        ],
      )
    },
    dataroomTermsLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=dataroom-price-list-${this.$i18n.locale.toUpperCase()}`
    },
  },
  created () {
    // In V2 we had a mandatory prop on chip-groups that auto-selected their first value
    // In V3 we need to do it manually because the prop doesn't exist anymore
    this.selectedDuration = this.durations[0]
    this.selectedData = this.data[0]
    this.onDataRoomSettingsChange()
  },
  methods: {
    ...mapActions('rooms', [GET_DATA_ROOM_CUSTOM_PRICING]),
    async onDataRoomSettingsChange () {
      let actualSelectedData = this.selectedData === ADDITIONAL_DATA_VALUE ? this.selectedAdditionalData : this.selectedData

      if (this.selectedDuration?.value && actualSelectedData.value && actualSelectedData.unit) {
        try {
          await this.GET_DATA_ROOM_CUSTOM_PRICING({
            duration: this.selectedDuration.value,
            unit: actualSelectedData.unit,
            storage: actualSelectedData.value,
          })

          if (this.dataRoomCustomPricing.hasDataroomPayPerUseSpecialOffer) {
            this.forceSelectedDurationFromSpecialOffer()
            this.forceSelectedDataFromSpecialOffer()
            actualSelectedData = this.selectedData
          }

          this.newDataRoomDuration = cloneDeep(this.selectedDuration)
          this.newDataRoomData = cloneDeep(actualSelectedData)
        } catch (e) {
          console.error(e)
        }
      }
    },
    forceSelectedDurationFromSpecialOffer () {
      // If the special offer has a duration that is not in the default durations list, we add it so it can be selected
      if (!this.durations.some(duration => duration.value === this.dataRoomCustomPricing.months)) {
        const newCustomMonthsCount = Number(this.dataRoomCustomPricing.months)
        const newDurationValue = {
          value: newCustomMonthsCount,
          text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: newCustomMonthsCount }, newCustomMonthsCount),
        }

        const newDurations = [...this.durations, newDurationValue]
        this.durations = newDurations.sort((a, b) => a.value - b.value)
      }

      this.durations.forEach(duration => {
        if (duration.value === this.dataRoomCustomPricing.months) {
          this.selectedDuration = duration
        }
      })
    },
    forceSelectedDataFromSpecialOffer () {
      let sizeToSet = this.dataRoomCustomPricing.size
      let unitToSet = this.dataRoomCustomPricing.unit

      const isSizeDataInDefaultList = this.data.some(
        data => this.getSizeInMb(data.value, data.unit) === this.getSizeInMb(sizeToSet, unitToSet),
      )
      // if the special offer has a size that is not in the default data list, we add it so it can be selected
      if (!isSizeDataInDefaultList) {
        if (unitToSet === 'Gb' && sizeToSet < 1) {
          sizeToSet = sizeToSet * 1000
          unitToSet = 'Mb'
        }

        const localizedUnit = this.$t(`projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.${unitToSet}`);

        const newDataValue = {
          value: sizeToSet,
          unit: unitToSet,
          text: `${sizeToSet} ${localizedUnit}`,
        }

        const newData = [...this.data, newDataValue]
        this.data = newData.sort((a, b) => {
          const aSizeInMb = this.getSizeInMb(a.value, a.unit)
          const bSizeInMb = this.getSizeInMb(b.value, b.unit)

          return aSizeInMb - bSizeInMb
        })
      }

      this.data.forEach(data => {
        if (data.value === sizeToSet && data.unit === unitToSet) {
          this.selectedData = data
        }
      })
    },
    getSizeInMb (size, unit) {
      return unit === 'Gb' ? size * 1000 : size
    },
    onSelectedAdditionalDataChange () {
      this.selectedData = ADDITIONAL_DATA_VALUE
      this.onDataRoomSettingsChange()

    },
    areSameSizeData(data1, data2) {
      return data1.value === data2.value && data1.unit === data2.unit
    },
  },
}
</script>

<style scoped lang="scss">
.pricing {
  padding: 20px 30px;
  border: 1px solid #9e9e9e;
  background: #fafafa;
  border-radius: 8px;

  &-amountPerMonth {
    font-size: 28px;
    font-weight: bold;
    margin-right: 4px;
  }
  &-underline {
    font-weight: 600;
    font-size: 16px;
    color: #4CAF50;
    text-decoration: line-through;
  }
}
.customDurationChip,
.customDataChip {
  &.v-chip--active {
    :deep(.v-input__slot input),
    :deep(.v-select__selection),
    :deep(.v-input__append-inner) {
      color: #fff;
    }
  }
}
:deep(.customDataSelect) {
  .v-input__control {
    min-height: 20px !important;
    margin-left: 4px;
    width: 80px;
    & .v-field__overlay {
      display: none;
    }
    & .v-field__input {
      padding-left: 0;
      padding-right: 0;
    }

    & .v-field__append-inner {
      padding-left: 0;
      & .v-icon {
        font-size: 16px;
      }
    }
    & .v-field__field {
      padding: 0;
      background-color: transparent;
      box-shadow: none !important;
      & .v-select__selection--coma {
        text-overflow: unset;
      }
      input::placeholder {
        color: #333333;
        opacity: 1;
      }
    }
  }
  &.v-input--is-focused {
    .v-input__append-inner .v-icon {
      color: #fff !important;
      caret-color: #fff !important;
    }
  }
}

.discount-title {
  position: relative;
  top: -20px;
  left: -30px;
  height: 35px;
  width: calc(100% + 60px);
  background-color: #91F21F66;

  display: flex;
  align-items:center;
  justify-content:center;

  font-weight: 600;
  font-size: 16px;
  color: #4CAF50;
}

.setup-dataroom-title {
  font-size: 16px;
  line-height: 24px;
  color: rgb(var(--v-theme-grey-900));
  font-weight: 500;
}

.offer-details-container {
  border: 1px solid rgb(var(--v-theme-grey-100));
}
</style>
